import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useOptionalApiClient } from "../api/ApiClientContext";
import { paths } from "../app/paths";
import type { SignInRedirectState } from "../app/guards/RouteGuard";
import { useSession } from "../app/session/SessionContext";
import { useOptionalAuthClient } from "./AuthSessionProvider";
import { ACTIVITY_EVENTS, IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from "./idle-timeout";
import { IdleWarningDialog } from "./IdleWarningDialog";

/**
 * AUTH-008 session handling not covered by the SDK itself: idle sign-out (15 minutes of no
 * activity, warned 60 seconds before) and API-401 resync (a server UNAUTHENTICATED response
 * while the browser still believes it is signed in, e.g. after admin-side revocation). Mounted
 * once in RootLayout so it applies across every route. Inert until the session is authenticated.
 */
export function IdleSessionMonitor() {
  const session = useSession();
  const auth = useOptionalAuthClient();
  const api = useOptionalApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const [warning, setWarning] = useState(false);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const authenticated = session.status === "authenticated";
  const signOutForRef = useRef<(reason: "idle_timeout" | "session_revoked") => void>(() => {
    // replaced below before it can be called; only exists to give the ref an initial value
  });

  useEffect(() => {
    signOutForRef.current = (reason) => {
      setWarning(false);
      const { pathname, search } = locationRef.current;
      const state: SignInRedirectState = { from: `${pathname}${search}`, reason };
      void auth?.signOut();
      void navigate(paths.signIn, { replace: true, state });
    };
  }, [auth, navigate]);

  const resetRef = useRef<() => void>(() => {
    // replaced below while the idle timers are active; a no-op the rest of the time
  });

  useEffect(() => {
    if (!authenticated || !auth) return undefined;
    let warnTimer: ReturnType<typeof setTimeout>;
    let logoutTimer: ReturnType<typeof setTimeout>;

    function reset() {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      setWarning(false);
      warnTimer = setTimeout(() => {
        setWarning(true);
      }, IDLE_TIMEOUT_MS - IDLE_WARNING_MS);
      logoutTimer = setTimeout(() => {
        signOutForRef.current("idle_timeout");
      }, IDLE_TIMEOUT_MS);
    }
    resetRef.current = reset;
    reset();

    for (const type of ACTIVITY_EVENTS) window.addEventListener(type, reset, { passive: true });
    return () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, reset);
    };
  }, [authenticated, auth]);

  useEffect(() => {
    if (!api || !auth) return undefined;
    return api.onUnauthenticated(() => {
      signOutForRef.current("session_revoked");
    });
  }, [api, auth]);

  if (!warning) return null;
  return (
    <IdleWarningDialog
      onStaySignedIn={() => {
        resetRef.current();
      }}
    />
  );
}
